/**
 * Derives the text/plain alternative from composed HTML.
 *
 * Every outgoing message carries both parts. That is not politeness: a
 * text-only alternative is what renders in clients configured to refuse HTML,
 * in notification previews, in `preview` fields like the one this app's own
 * message list shows, and in accessibility tools. Sending HTML alone produces
 * messages that look empty in all of those.
 *
 * This is a deliberate, small conversion rather than `textContent`: block
 * boundaries have to become newlines or the whole message arrives as one
 * paragraph, links have to keep their targets, and quoted text has to keep its
 * `>` markers so a reply chain still reads as one.
 */
export function htmlToText(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");

  const walk = (node: Node, quote: number): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      // Collapse authored whitespace the way a renderer would.
      return (node.textContent ?? "").replace(/\s+/g, " ");
    }
    if (!(node instanceof HTMLElement)) return "";

    const tag = node.tagName.toLowerCase();
    if (tag === "br") return "\n";
    if (tag === "style" || tag === "script") return "";

    if (tag === "img") {
      const alt = node.getAttribute("alt");
      return alt ? `[${alt}]` : "";
    }

    const depth = tag === "blockquote" ? quote + 1 : quote;
    let inner = Array.from(node.childNodes).map((child) => walk(child, depth)).join("");

    if (tag === "a") {
      const href = node.getAttribute("href") ?? "";
      const label = inner.trim();
      // Only append the URL when it says something the text does not.
      if (href && label && !href.startsWith("mailto:") && label !== href) return `${label} <${href}>`;
      return label || href;
    }

    if (tag === "li") {
      const marker = node.parentElement?.tagName.toLowerCase() === "ol"
        ? `${Array.from(node.parentElement.children).indexOf(node) + 1}. `
        : "- ";
      return `${marker}${inner.trim()}\n`;
    }

    if (tag === "blockquote") {
      return inner
        .split("\n")
        .map((line) => (line.trim() ? `> ${line}` : ">"))
        .join("\n") + "\n";
    }

    // Cells need a separator or a table reads as "MetricValueRevenueUp 14%".
    // Tab is what mail clients have always used and what spreadsheets paste as.
    if (tag === "td" || tag === "th") {
      const cell = inner.trim();
      const isLast = !node.nextElementSibling;
      return isLast ? cell : `${cell}\t`;
    }

    const BLOCK = /^(p|div|h[1-6]|ul|ol|pre|table|tr|section|article|header|footer|hr)$/;
    if (BLOCK.test(tag)) {
      if (tag === "hr") return "\n---\n";
      inner = inner.replace(/\s+$/, "");
      return inner ? `${inner}\n` : "";
    }

    return inner;
  };

  return walk(doc.body, 0)
    // Never more than one blank line, and no leading or trailing space.
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Escapes text so it can be embedded in the HTML part. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Plain text (a quoted reply, say) as the HTML part's equivalent. */
export function textToHtml(text: string): string {
  return escapeHtml(text)
    .split("\n")
    .map((line) => (/^\s*&gt;/.test(line)
      ? `<blockquote>${line.replace(/^\s*(&gt;\s?)+/, "")}</blockquote>`
      : `<div>${line || "<br>"}</div>`))
    .join("");
}
